/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/managerTasksApi.ts
 * PURPOSE: BR-WF-003 Manager Task Assignment ("Giao việc") — API client cho
 *          roster chọn NV, giao việc hàng loạt, danh sách chờ duyệt, duyệt/từ chối
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-12
 * COMPLIANCE: SDLC 6.5.0 + Sprint Governance Rule 3 (Zero Mock Tolerance)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Management — Manager giao việc cho NV
 * - Impact: Revenue Critical — vận hành hàng ngày tại chi nhánh
 * - Stakeholders: Branch/Shift Managers, Employees
 *
 * @technical-architecture
 * - Layer: Data Access Layer (API Services)
 * - Pattern: Axios HTTP client (apiV2), TypeScript strict types
 * - Dependencies: axios (via apiV2 from config)
 *
 * @cross-references
 * - Backend: backend/app/api/v2/tasks.py
 * - Design: docs/01-planning/01-requirements/09-workflows/BR-WF-003-manager-task-assignment.md
 * ===============================================================================
 */

import { apiV2 } from "./config";

export interface TaskRosterShift {
  start_time: string;
  end_time: string;
}

export interface TaskRosterItem {
  id: string;
  employee_code: string | null;
  full_name: string;
  position: string | null;
  work_area_id: string | null;
  work_area_name: string;
  has_shift_today: boolean;
  shift: TaskRosterShift | null;
}

export interface TaskRosterResponse {
  date: string;
  items: TaskRosterItem[];
}

export type DeadlineMarker = "start" | "mid" | "end" | "custom";

/** 1 nội dung công việc trong modal Giao việc — mỗi item có tiêu đề + NV riêng. */
export interface BulkTaskItemPayload {
  title: string;
  description?: string;
  assigned_to_ids: string[];
}

export interface BulkCreateTaskPayload {
  items: BulkTaskItemPayload[];
  shift_dates: string[];
  deadline_marker: DeadlineMarker;
  /** Bắt buộc khi deadline_marker="custom" — giờ hạn chót tự nhập "HH:MM" (giờ VN). */
  deadline_time?: string;
  requires_approval?: boolean;
}

export interface BulkCreateTaskResultItem {
  title: string;
  shift_date: string;
  assigned_to_id: string;
  task_id: string | null;
  deadline: string | null;
  error: string | null;
}

export interface BulkCreateTaskResponse {
  created: BulkCreateTaskResultItem[];
  skipped: BulkCreateTaskResultItem[];
}

export interface ShiftTaskItem {
  id: string;
  tenant_id: string;
  branch_id: string;
  task_type: string;
  status: string;
  title: string;
  description: string | null;
  created_by: string;
  assigned_to: string;
  shift_date: string;
  deadline: string;
  acknowledged_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  completion_note: string | null;
  completion_photo_url: string | null;
  escalated_from: string | null;
  created_at: string;
  updated_at: string | null;
  requires_approval: boolean;
  approval_status: "PENDING" | "APPROVED" | "REJECTED" | null;
  approved_by: string | null;
  approved_at: string | null;
  rejection_reason: string | null;
  deadline_marker: DeadlineMarker | null;
}

export interface TaskListResponse {
  items: ShiftTaskItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface TaskHistoryItem extends ShiftTaskItem {
  audit_trail: unknown[];
}

export interface TaskHistoryResponse {
  items: TaskHistoryItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface ApproveTaskPayload {
  approved: boolean;
  notes?: string;
}

export interface EmployeeTaskSummaryItem {
  tenant_user_id: string;
  full_name: string;
  total: number;
  completed: number;
  approved: number;
  rejected: number;
  pending_confirm: number;
  pending_approval: number;
}

export interface EmployeeTaskSummaryResponse {
  date_from: string;
  date_to: string;
  items: EmployeeTaskSummaryItem[];
}

export const managerTasksApi = {
  getRoster: async (date?: string): Promise<TaskRosterResponse> => {
    const response = await apiV2.get<TaskRosterResponse>("/tasks/roster", {
      params: date ? { date } : undefined,
    });
    return response.data;
  },

  bulkCreate: async (payload: BulkCreateTaskPayload): Promise<BulkCreateTaskResponse> => {
    const response = await apiV2.post<BulkCreateTaskResponse>("/tasks/bulk", payload);
    return response.data;
  },

  getPendingApproval: async (limit = 50, offset = 0): Promise<TaskListResponse> => {
    const response = await apiV2.get<TaskListResponse>("/tasks/pending-approval", {
      params: { limit, offset },
    });
    return response.data;
  },

  approve: async (taskId: string, payload: ApproveTaskPayload): Promise<ShiftTaskItem> => {
    const response = await apiV2.put<ShiftTaskItem>(`/tasks/${taskId}/approve`, payload);
    return response.data;
  },

  getHistory: async (params?: {
    date_from?: string;
    date_to?: string;
    status?: string;
    assigned_to?: string;
    limit?: number;
    offset?: number;
  }): Promise<TaskHistoryResponse> => {
    const response = await apiV2.get<TaskHistoryResponse>("/tasks/history", { params });
    return response.data;
  },

  getSummaryByEmployee: async (params?: {
    date_from?: string;
    date_to?: string;
  }): Promise<EmployeeTaskSummaryResponse> => {
    const response = await apiV2.get<EmployeeTaskSummaryResponse>("/tasks/summary-by-employee", { params });
    return response.data;
  },
};

/** Parse completion_photo_url — có thể là 1 URL đơn (luồng cũ) hoặc mảng JSON
 * base64 (BR-WF-003 nộp ảnh hàng loạt, xem §4.4 hướng b). */
export function parseTaskPhotos(completionPhotoUrl: string | null): string[] {
  if (!completionPhotoUrl) return [];
  try {
    const parsed = JSON.parse(completionPhotoUrl);
    if (Array.isArray(parsed)) return parsed;
  } catch {
    // không phải JSON — coi là 1 URL/base64 đơn
  }
  return [completionPhotoUrl];
}
